import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as GstIcon,
  Category as TypeIcon,
  LocalOffer as BrandIcon,
  Storefront,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import { request } from '../../../../../utils/request'; // Adjust path as needed
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { Vendor } from '../../../types';
import { usePermissions } from '../../../../../utils/usePermissions';
import { baseUrlImage } from '../../../../../utils/apiClient';

const VendorDetail = () => {
  const { canUpdateVendors } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await request({
          url: `/vendor/get-vendor/${id}`,
          method: 'get',
        });

        if (response.vendor) {
          setVendor(response.vendor);
        } else {
          setError('Vendor not found');
        }
      } catch (error: any) {
        console.error('Failed to fetch vendor:', error);
        setError(error || 'Failed to load vendor details');
        toast.error('Failed to load vendor details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchVendor();
    }
  }, [id]);

  const handleEdit = () => {
    if (vendor) {
      navigate(`/vendors/vendor/editVendor/${vendor._id}`);
    }
  };

  // Responsive layout helper
  const renderTwoColumnLayout = (leftContent: React.ReactNode, rightContent: React.ReactNode) => {
    if (isMobile || isTablet) {
      return (
        <Stack spacing={2}>
          {leftContent}
          {rightContent}
        </Stack>
      );
    }

    return (
      <Box display="flex" gap={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {leftContent}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {rightContent}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading vendor details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/vendors/vendor')}
        >
          Back to Vendors
        </Button>
      </Box>
    );
  }

  if (!vendor) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Alert severity="warning">
          Vendor not found
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/vendors/vendor')}
          sx={{ mt: 2 }}
        >
          Back to Vendors
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Brandcrumb />
        <Chip
          onClick={() => navigate(-1)}
          icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
          label="Back"
          variant="filled"
          color="secondary"
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>

      {/* Header Card - Responsive */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50, flexShrink: 0 }}>
                <Storefront sx={{ fontSize: 24 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight="bold"
                  noWrap
                  sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                >
                  {vendor.vendorName}
                </Typography>
                {vendor.nickName && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {vendor.nickName}
                  </Typography>
                )}
                <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <Chip
                    label={vendor.status}
                    color={vendor.status === 'active' ? 'success' : 'error'}
                    size="small"
                    sx={{ height: 24 }}
                  />
                  {vendor.role && (
                    <Chip
                      label={`Role: ${vendor.role}`}
                      color='secondary'
                      size="small"
                      sx={{ height: 24 }}
                    />
                  )}
                  <Chip
                    label={`GST: ${vendor.withGST ? "Yes" : "No"}`}
                    color='secondary'
                    size="small"
                    sx={{ height: 24 }}
                  />
                </Box>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ mt: isMobile ? 1 : 0 }}>
              <Tooltip title="Add Product for this Vendor" arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={!isMobile && <AddIcon />}
                  onClick={() => navigate('/inventory/addProduct', {
                    state: {
                      vendorId: vendor._id,
                      source: 'vendor'
                    }
                  })}
                  size="small"
                  sx={{ minWidth: isMobile ? 120 : 'auto' }}
                >
                  {isMobile ? 'Add Product' : 'Add Product'}
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color='secondary'
                size='small'
                onClick={() => navigate(`/vendors/vendor/dailyLedger/${vendor._id}`)}
                sx={{ minWidth: isMobile ? 120 : 'auto' }}
              >
                Daily Ledger
              </Button>
              {canUpdateVendors() && (
                <Tooltip title="Edit Vendor" arrow>
                  <IconButton
                    color="primary"
                    onClick={handleEdit}
                    size="small"
                  >
                    <EditIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Content - Two Column Layout */}
      {renderTwoColumnLayout(
        /* Left Column */
        <Stack spacing={1.5}>
          {/* Basic Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Basic Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <TypeIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0, mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Type:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {vendor.type.map((type, index) => (
                        <Chip
                          key={index}
                          label={type}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                  <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 60 }}>
                    Location:
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    {vendor.location}
                  </Typography>
                </Box>

                {vendor.gstNumber && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <GstIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 60 }}>
                      GST No:
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" noWrap sx={{ flex: 1 }}>
                      {vendor.gstNumber}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Contact Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Contact Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1.5}>
                {/* Phone Numbers */}
                <Box>
                  <Typography variant="caption" fontWeight="bold" gutterBottom>
                    Phone Numbers
                  </Typography>
                  <Stack spacing={0.75}>
                    {vendor.contact.phone.map((phone, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1}>
                        <PhoneIcon sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }} />
                        <Chip
                          label={phone.label}
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 50, height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" fontFamily="monospace" noWrap sx={{ flex: 1 }}>
                          {phone.phoneNo}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {/* Email Addresses */}
                <Box>
                  <Typography variant="caption" fontWeight="bold" gutterBottom>
                    Email Addresses
                  </Typography>
                  <Stack spacing={0.75}>
                    {vendor.contact.email.map((email, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1}>
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {email}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>,

        /* Right Column */
        <Stack spacing={1.5}>
          {/* Address Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Address
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1}>
                {vendor.address.line1 && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {vendor.address.line1}
                  </Typography>
                )}
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {vendor.address.city && (
                    <Chip label={vendor.address.city} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {vendor.address.state && (
                    <Chip label={vendor.address.state} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {vendor.address.country && (
                    <Chip label={vendor.address.country} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {vendor.address.pinCode && (
                    <Chip label={vendor.address.pinCode} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Associated Brands Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <BrandIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  Associated Brands
                </Typography>
                <Chip
                  label={`${vendor.brands.length}`}
                  size="small"
                  color="secondary"
                  sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {vendor.brands.map((brand, index) => (
                  <Chip
                    key={index}
                    avatar={
                      brand.brandLogo ? (
                        <Avatar
                          src={brand.brandLogo ? baseUrlImage + brand.brandLogo : 'https://cdn-icons-png.freepik.com/512/9942/9942310.png'}
                          alt={brand.brandName}
                          sx={{ width: 20, height: 20 }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: 'secondary.main',
                            fontSize: '0.6rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {brand.brandName.charAt(0).toUpperCase()}
                        </Avatar>
                      )
                    }
                    label={brand.brandName}
                    color="secondary"
                    variant="filled"
                    size="small"
                    sx={{
                      mb: 0.5,
                      '& .MuiChip-label': {
                        fontSize: '0.75rem',
                        px: 0.75,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100px'
                      }
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Additional Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Additional Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Created:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {new Date(vendor.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Last Updated:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {new Date(vendor.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
    </>
  );
};

export default VendorDetail;